/**
 * Opengater API client — direct-from-browser calls to the five Opengater
 * microservices, authorised by the section's own OG access token.
 *
 * Shape mirrors `@/api/client` and `@/api/auth-client`:
 *   - Bearer token from the `og_` store on every request
 *   - one refresh-and-retry on 401; a 403 is surfaced without ending the session
 *   - reuses the shared `ApiError` so pages can `instanceof`-check uniformly
 *
 * Difference from the panel's client: on terminal auth failure we do NOT
 * `window.location`-redirect to an external hosted login. The Opengater login
 * lives INSIDE the SPA (`OpengaterLogin`), so we clear the OG tokens and emit
 * `OG_AUTH_EXPIRED_EVENT`; `OgAuthProvider` listens and flips the section back
 * to its login screen without disturbing the rest of the panel.
 */
import { ApiError } from '../client';
import { ogServiceBase, ogServiceLabel } from './services';
import { getOgAccessToken, clearOgTokens, OG_AUTH_EXPIRED_EVENT } from './tokens';
import { refreshOgToken } from './refresh';
import { emitAuthorizationDenied } from '@/auth/authorizationEvents';
export { OG_AUTH_EXPIRED_EVENT };
function emitAuthExpired() {
    clearOgTokens();
    window.dispatchEvent(new CustomEvent(OG_AUTH_EXPIRED_EVENT));
}
async function doFetch(service, path, init, attempt) {
    const base = ogServiceBase(service);
    if (!base) {
        throw new ApiError(0, null, `${ogServiceLabel(service)} service is not configured (missing env URL)`);
    }
    const headers = new Headers(init.headers);
    const token = getOgAccessToken();
    if (token)
        headers.set('Authorization', `Bearer ${token}`);
    if (init.body !== undefined && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(base + path, {
        ...init,
        headers,
        body: init.body === undefined
            ? undefined
            : init.body instanceof FormData
                ? init.body
                : JSON.stringify(init.body),
    });
    // 401 = token expired/invalid. 403 = valid token, insufficient role. Only a
    // 401 gets one refresh attempt; the server remains the authority for 403.
    if (res.status === 401 && attempt === 0) {
        const newToken = await refreshOgToken();
        if (newToken)
            return doFetch(service, path, init, 1);
        emitAuthExpired();
        return res;
    }
    if (res.status === 403) {
        if (!(service === 'auth' && (path === '/users/me' || path === '/users/me/short'))) {
            emitAuthorizationDenied('opengater');
        }
        return res;
    }
    if (res.status === 401 && attempt > 0) {
        emitAuthExpired();
    }
    return res;
}
export async function apiOg(service, path, init = {}) {
    const res = await doFetch(service, path, init, 0);
    // Read the body as text ONCE, then parse JSON only when there's actually
    // something to parse. This is the safe path for empty responses — DELETE /
    // PUT often reply 204, or 200 with an empty body (and sometimes still an
    // application/json content-type). `res.json()` throws on an empty body, so
    // parsing unconditionally would make every such mutation look like it failed.
    const raw = await res.text();
    const ct = res.headers.get('content-type') || '';
    let body = raw;
    if (raw && ct.includes('application/json')) {
        try {
            body = JSON.parse(raw);
        }
        catch {
            body = raw; // malformed JSON — surface the text rather than throwing
        }
    }
    if (!res.ok) {
        const retryAfter = res.headers.get('retry-after');
        let retryAfterSeconds = null;
        if (retryAfter) {
            const numeric = Number(retryAfter);
            if (Number.isFinite(numeric) && numeric >= 0) {
                retryAfterSeconds = Math.ceil(numeric);
            }
            else {
                const at = Date.parse(retryAfter);
                if (!Number.isNaN(at))
                    retryAfterSeconds = Math.max(0, Math.ceil((at - Date.now()) / 1000));
            }
        }
        throw new ApiError(res.status, body, `${res.status} ${res.statusText}`, retryAfterSeconds);
    }
    if (res.status === 204 || raw === '')
        return undefined;
    return body;
}
